import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTimer } from '../hooks/useTimer';
import { generateProblem } from '../utils/mathEngine';
import TimerRing from './TimerRing';
import SlotCounter from './SlotCounter';
import FractionDisplay from './FractionDisplay';
import Balloon from './Balloon';
import './GameScreen.css';

let balloonIdCounter = 0;

const CORRECT_VOICES = [
  "Yeah!", "Well done!", "Nice choice!", "Absolutely right!",
  "You were quick!", "Perfect!", "Great shot!", "Bullseye!",
  "Nailed it!", "Excellent!", "Yes, Kaboom!", "Yes!",
  "Direct hit!", "Winner, winner!", "Boom, baby!",
  "On fire!", "Unstoppable!", "Legendary!",
  "Genius move!", "Math wizard!", "Crushed it!",
  "Too easy!", "Like a boss!", "Flawless!"
];

const WRONG_VOICES = [
  "Oh no!", "That was wrong.", "Wrong guess!", "Missed!",
  "Not quite!", "Try again!", "Oops!", "So close!",
  "Fail!", "You lose!", "Wasted!", "Destroyed!",
  "Game over, man!", "Critical miss!", "Nope!",
  "Not even close!", "Better luck next time!",
  "That hurt!", "Terrible choice!", "Epic fail!"
];

function speak(text) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = 'en-US';
  utter.rate = 1.1;
  utter.pitch = 1.0;
  utter.volume = 0.8;
  const voices = window.speechSynthesis.getVoices();
  const englishVoice = voices.find(v => v.lang.startsWith('en'));
  if (englishVoice) utter.voice = englishVoice;
  window.speechSynthesis.speak(utter);
}

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

export default function GameScreen({ settings, arcadeMode, onGameEnd }) {
  const [score, setScore] = useState(0);
  const [problem, setProblem] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [isEnding, setIsEnding] = useState(false);
  const [balloons, setBalloons] = useState([]);
  const [arcadeEffects, setArcadeEffects] = useState([]); // {id, type:'stars'|'slime', btnIdx}
  const [muzzleFlash, setMuzzleFlash] = useState(null); // {x, y}

  const musicRef = useRef(null);
  const arcadeIdRef = useRef(0);

  const scoreRef = useRef(score);
  const questionsRef = useRef(questionsAnswered);
  const correctRef = useRef(correctCount);
  scoreRef.current = score;
  questionsRef.current = questionsAnswered;
  correctRef.current = correctCount;

  // Start/stop background music
  useEffect(() => {
    if (!arcadeMode) return;
    const audio = new Audio(process.env.PUBLIC_URL + '/Turrican Skyline.mp3');
    audio.loop = true;
    audio.volume = 0.3;
    musicRef.current = audio;
    // Warm up speech synthesis voices
    window.speechSynthesis?.getVoices();
    const playPromise = audio.play();
    if (playPromise) playPromise.catch(() => {});
    return () => {
      audio.pause();
      audio.src = '';
      musicRef.current = null;
    };
  }, [arcadeMode]);

  // Stop music when game ends
  useEffect(() => {
    if (isEnding && musicRef.current) {
      const audio = musicRef.current;
      const fadeOut = setInterval(() => {
        if (audio.volume > 0.05) {
          audio.volume = Math.max(0, audio.volume - 0.03);
        } else {
          clearInterval(fadeOut);
          audio.pause();
        }
      }, 80);
      return () => clearInterval(fadeOut);
    }
  }, [isEnding]);

  const handleTimeUp = useCallback(() => {
    setIsEnding(true);
    setTimeout(() => {
      onGameEnd({
        score: scoreRef.current,
        questionsAnswered: questionsRef.current,
        correctCount: correctRef.current,
      });
    }, 1200);
  }, [onGameEnd]);

  const { progress, formatTime, start, isRunning } = useTimer(settings.duration, handleTimeUp);

  const getRandomOperation = useCallback(() => {
    const ops = settings.operations;
    return ops[Math.floor(Math.random() * ops.length)];
  }, [settings.operations]);

  const loadNewProblem = useCallback(() => {
    setFeedback(null);
    setSelectedIdx(null);
    setArcadeEffects([]);
    const operation = getRandomOperation();
    setProblem(generateProblem({
      ...settings,
      operation,
    }));
  }, [settings, getRandomOperation]);

  useEffect(() => {
    start();
    loadNewProblem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeBalloon = useCallback((id) => {
    setBalloons(prev => prev.filter(b => b.id !== id));
  }, []);

  const handleAnswer = (option, index, event) => {
    if (feedback !== null || !isRunning) return;

    // Muzzle flash at click position
    if (arcadeMode && event) {
      const rect = event.currentTarget.getBoundingClientRect();
      setMuzzleFlash({
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      });
      setTimeout(() => setMuzzleFlash(null), 300);
    }

    setSelectedIdx(index);
    setQuestionsAnswered(prev => prev + 1);

    const balloonId = ++balloonIdCounter;
    const arcadeEffectId = ++arcadeIdRef.current;

    if (option.correct) {
      setFeedback('correct');
      setScore(prev => prev + 1);
      setCorrectCount(prev => prev + 1);
      setBalloons(prev => [...prev, { id: balloonId, type: 'correct', points: 1 }]);
      if (arcadeMode) {
        setArcadeEffects([{ id: arcadeEffectId, type: 'stars', btnIdx: index }]);
        speak(pickRandom(CORRECT_VOICES));
      }
    } else {
      setFeedback('wrong');
      setScore(prev => prev - 2);
      setBalloons(prev => [...prev, { id: balloonId, type: 'wrong', points: -2 }]);
      if (arcadeMode) {
        setArcadeEffects([{ id: arcadeEffectId, type: 'slime', btnIdx: index }]);
        speak(pickRandom(WRONG_VOICES));
      }
    }

    setTimeout(() => {
      loadNewProblem();
    }, 1200);
  };

  const renderProblem = () => {
    if (!problem) return null;

    if (problem.type === 'natural') {
      return <div className="problem-text">{problem.display} = ?</div>;
    }

    if (problem.type === 'fraction-simplify') {
      return (
        <div className="problem-fraction-container">
          <div className="problem-instruction">{problem.instruction}</div>
          <div className="problem-fractions">
            <FractionDisplay num={problem.fraction1.num} den={problem.fraction1.den} />
            <span className="problem-equals">= ?</span>
          </div>
        </div>
      );
    }

    if (problem.type === 'fraction-expand') {
      return (
        <div className="problem-fraction-container">
          <div className="problem-instruction">{problem.instruction}</div>
          <div className="problem-fractions">
            <FractionDisplay num={problem.fraction1.num} den={problem.fraction1.den} />
            <span className="problem-equals">= ?</span>
          </div>
        </div>
      );
    }

    return (
      <div className="problem-fraction-container">
        <div className="problem-fractions">
          <FractionDisplay num={problem.fraction1.num} den={problem.fraction1.den} mixed={problem.isMixed} />
          <span className="problem-operator">{problem.operator}</span>
          <FractionDisplay num={problem.fraction2.num} den={problem.fraction2.den} mixed={problem.isMixed} />
          <span className="problem-equals">= ?</span>
        </div>
      </div>
    );
  };

  const renderOption = (option) => {
    if (option.type === 'natural') {
      return <span className="option-value">{option.value}</span>;
    }
    return (
      <FractionDisplay
        num={option.value.num}
        den={option.value.den}
        size="small"
        mixed={option.mixed}
      />
    );
  };

  const getArcadeEffectForBtn = (idx) => {
    return arcadeEffects.find(e => e.btnIdx === idx);
  };

  if (!problem) return null;

  return (
    <div className={`game-container ${isEnding ? 'game-ending' : ''} ${arcadeMode ? 'arcade-mode' : ''}`}>
      {/* Balloons overlay */}
      {balloons.map(b => (
        <Balloon
          key={b.id}
          id={b.id}
          type={b.type}
          points={b.type === 'correct' ? `${b.points}` : `${b.points}`}
          onRemove={removeBalloon}
        />
      ))}

      {/* Arcade mode banner */}
      {arcadeMode && (
        <div className="arcade-banner">ARCADE MODE</div>
      )}

      {/* Top bar */}
      <div className="game-top-bar">
        <TimerRing progress={progress} timeString={formatTime()} />
        <SlotCounter value={score} />
        <div className="game-stats">
          <div className="stat">
            <span className="stat-num">{questionsAnswered}</span>
            <span className="stat-label">Aufgaben</span>
          </div>
          <div className="stat">
            <span className="stat-num">{correctCount}</span>
            <span className="stat-label">Richtig</span>
          </div>
        </div>
      </div>

      {/* Problem area */}
      <div className={`problem-area ${feedback ? `feedback-${feedback}` : ''}`}>
        <div className="problem-card">
          {renderProblem()}
        </div>
      </div>

      {/* Answer options */}
      <div className="options-grid">
        {problem.options.map((option, idx) => {
          const effect = getArcadeEffectForBtn(idx);
          const isSlime = effect?.type === 'slime';
          const isStars = effect?.type === 'stars';

          return (
            <button
              key={idx}
              className={`option-btn-game ${
                selectedIdx === idx
                  ? option.correct ? 'selected-correct' : 'selected-wrong'
                  : feedback !== null && option.correct ? 'reveal-correct' : ''
              } ${isSlime ? 'arcade-slime' : ''} ${isStars ? 'arcade-stars' : ''}`}
              onClick={(e) => handleAnswer(option, idx, e)}
              disabled={feedback !== null}
            >
              {renderOption(option)}

              {/* Muzzle flash on clicked button */}
              {arcadeMode && selectedIdx === idx && muzzleFlash && (
                <span
                  className="muzzle-flash"
                  style={{ left: muzzleFlash.x, top: muzzleFlash.y }}
                />
              )}

              {/* Bouncing stars for correct */}
              {isStars && (
                <div className="stars-container">
                  {[...Array(8)].map((_, i) => (
                    <span key={i} className="bouncing-star" style={{
                      '--star-delay': `${i * 0.08}s`,
                      '--star-x': `${(i - 3.5) * 14}%`,
                      '--star-angle': `${Math.random() * 360}deg`,
                    }}>&#9733;</span>
                  ))}
                </div>
              )}

              {/* Slime explosion for wrong */}
              {isSlime && (
                <div className="slime-container">
                  {[...Array(12)].map((_, i) => (
                    <span key={i} className="slime-particle" style={{
                      '--slime-angle': `${i * 30}deg`,
                      '--slime-dist': `${40 + Math.random() * 40}px`,
                      '--slime-delay': `${Math.random() * 0.15}s`,
                      '--slime-size': `${6 + Math.random() * 10}px`,
                    }} />
                  ))}
                  <div className="slime-splat" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
